import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Box,
  FormInputText,
  InputLabel,
  Modal,
  Select,
  Svg,
  Text,
} from '@hopskipdrive/react-components';
import { QueryParamsForAssignRiders } from '@rideiq/organizers/types';
import { useDeactivateReason } from '@rideiq/riders/features/DeactivateRider/useDeactivateReason';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { colorTokens } from '@rideiq/shared/components/variables';
import styled from 'styled-components';
import * as Yup from 'yup';

import { useBulkDeactivateRiders } from './useBulkDeactivateRiders';

const schema = Yup.object({
  reasonKey: Yup.string().required('Please select a deactivation reason'),
});

type FormValues = Yup.InferType<typeof schema>;

interface BulkDeactivateModalProps {
  open: boolean;
  selectedRiderIds: number[];
  isAllFilteredSelected: boolean;
  totalCount: number;
  onClose: () => void;
  onSuccess: () => void;
  queryParams: QueryParamsForAssignRiders;
}

export const BulkDeactivateModal = ({
  open,
  selectedRiderIds,
  isAllFilteredSelected,
  totalCount,
  onClose,
  onSuccess,
  queryParams,
}: BulkDeactivateModalProps) => {
  const { deactivateReasons } = useDeactivateReason();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: { reasonKey: undefined },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const { submit, riderCount, riderLabel } = useBulkDeactivateRiders({
    selectedRiderIds,
    isAllFilteredSelected,
    totalCount,
    queryParams,
    onSuccess,
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Modal.Title>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <PageHeading variant="h1" fontWeight={700}>{`Deactivate ${riderCount} ${riderLabel}`}</PageHeading>
            <Text mt={1} textColor="secondaryText">
              Deactivated riders will no longer appear in the list of active riders and cannot be added to other rides.
              You can reactivate these riders later, but any ride cancellations cannot be undone.
            </Text>
          </Box>
          <CloseButton onClick={handleClose}>
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Text mb={1} textColor="secondaryText">
          If you deactivate all riders on a ride, that ride will be cancelled.
        </Text>
        <Text mb={2} textColor="secondaryText">
          If a shared ride still has active riders, the deactivated rider(s) will be removed and the ride will continue.
        </Text>
        <InputLabel htmlFor="reasonKey" fieldtype="required" style={{ fontWeight: 300 }}>
          Reason
        </InputLabel>
        <Controller
          control={control}
          name="reasonKey"
          render={({ field: { onChange, value } }) => (
            <Select
              options={deactivateReasons}
              value={value}
              setValue={onChange}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <FormInputText
                  {...params}
                  variant="outlined"
                  placeholder="Select a reason"
                  fullWidth
                  error={!!errors.reasonKey}
                  helperText={errors.reasonKey?.message}
                />
              )}
            />
          )}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button color="tertiary" onClick={handleClose}>
          Never mind
        </Button>
        <Button color="primary" onClick={handleSubmit(({ reasonKey }) => submit(reasonKey))} loading={isSubmitting}>
          {isSubmitting ? 'Deactivating...' : 'Confirm'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const CloseButton = styled(Link)`
  display: inline-flex;
  margin-left: auto;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: #ddebfa;
    svg {
      color: ${colorTokens.text.hover};
    }
  }

  &:focus-visible {
    outline: 2px solid ${colorTokens.primary.main};
    outline-offset: 2px;
  }
`;
